import { getTokenFromLocalStorage } from "../../utilities/tokenUtils";
import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import {
  Button,
  Dropdown,
  Form,
  InputGroup,
  Navbar,
  Nav,
  Container,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { LOGOUT } from "constants/actionTypes";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import { useCountry } from "../../context/CountryContext.js";
import { getMarket } from "../../config/markets.js";
import CountryFlag from "../CountryFlag.js";
import BranchPicker from "../branch/BranchPicker.js";
import { useBranchScope } from "../../context/BranchScopeContext.js";
import useVendorOrderRooms from "../../hooks/useVendorOrderRooms.js";
import { vendorOwnerIdOf } from "../../utilities/vendorScope.js";
import io from "socket.io-client";
import socketAuthOptions from "../../utilities/socketAuth.js";
import {
  getSubscriptionState,
  WEB_PUSH_STATE_EVENT,
} from "../../utilities/webPush.js";
import {
  ORDER_ALERTS_CONFIG_EVENT,
  ORDER_ALERTS_ENABLED_KEY,
  ORDER_ALERTS_STATE_EVENT,
  readOrderAlertsEnabled,
} from "../../utilities/notificationState.js";
import { useSellerReviewQueueSync } from "../../hooks/useSellerReviewQueue.js";
import { useMenuBadgeSync } from "../../hooks/useMenuBadges";
import useOrderAlerts from "../../hooks/useOrderAlerts.js";
import staffOrderTone from "../../assets/img/newOrderNotification.mp3";
import vendorOrderTone from "../../assets/img/restaurant_notification_beep.mp3";
import useAttentionNudge from "../../hooks/useAttentionNudge.js";
import AttentionBanner from "../AttentionBanner.js";
import {
  fetchNotificationClientConfig,
  isEventChannelEnabled,
} from "../../utilities/notificationClientConfig.js";
import {
  PENDING_SELLERS_PATH,
  canReviewSellers,
  describeSeller,
  formatPendingSellersMessage,
  isArrivalReason,
  isDepartureReason,
} from "../../utilities/sellerReviewQueue.js";
import "./AdminNavbar.css";

const API_URL = process.env.REACT_APP_SERVER_URL || '';

function AdminNavbar() {
  const [collapseOpen, setCollapseOpen] = useState(false);
  const [user, setUser] = useState(getUserFromLocalStorage());
  const [notifications, setNotifications] = useState([]);
  const [socket, setSocket] = useState(null);
  const { hasBranches, isBranchScoped, noBranchAccess } = useBranchScope();

  // A vendor login's live order rooms (4 Oct 2026): one join that names the
  // owner, so a team member hears the business's orders (they named their
  // own id before and heard nothing) and a branch member hears only theirs.
  const isSellerLogin = user?.result?.userType === "SELLER" && !user?.result?.partnerId;
  const vendorRole = user?.result?.role;
  const isLusukuStaff = user?.result?.userType === "LUSUKU STAFF";

  // ── The order alert sound, once, for every page ───────────────────────────
  // Until 17 Oct 2026 the sound engine (useOrderAlerts) was mounted inside
  // each dashboard VIEW, so it existed only while that view was on screen:
  // open an order, a rider page, anything else, and a new order arrived in
  // silence even with "Notifications On". The navbar is on every signed-in
  // page and already holds the socket the server pushes order events to, so
  // the engine lives here and rings only on those server events: the
  // platform's `newOrderAlert` for staff, the vendor and partner room events
  // below for everyone else. Nothing is inferred client-side from counts.
  const alerts = useOrderAlerts({
    soundUrl: isLusukuStaff ? staffOrderTone : vendorOrderTone,
  });
  const notifyRef = useRef(alerts.notify);
  notifyRef.current = alerts.notify;
  useVendorOrderRooms(socket, isSellerLogin ? vendorOwnerIdOf(user) : null, {
    // A server older than this dashboard never answers: the old joins.
    fallback: (s) => {
      const ownerId = vendorOwnerIdOf(user);
      if (vendorRole === "restaurantVendor") s.emit("joinRestaurantVendorRoom", ownerId);
      else if (vendorRole === "healthcareVendor") s.emit("joinHealthcareVendorRoom", ownerId);
      else s.emit("joinVendorRoom", ownerId);
    },
  });
  const [assignedOrdersCount, setAssignedOrdersCount] = useState(0);
  const [webPushState, setWebPushState] = useState({
    supported: true,
    subscribed: false,
    configured: false,
    enabled: false,
  });
  const [orderAlertsEnabled, setOrderAlertsEnabled] = useState(
    readOrderAlertsEnabled
  );
  // WhatsApp / chat inbox notifications (separate stream from order alerts)
  const [chatNotifs, setChatNotifs] = useState([]);
  const assignedCountFetchedRef = useRef(false);

  const cart = useSelector((state) => state.cart);
  const { cartItems } = cart;

  const getCartCount = () => {
    return cartItems.reduce((qty, item) => Number(item.qty) + qty, 0);
  };

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  // ── Country context (multi_national) ──────────────────────────────────────
  // `canSwitch` is true ONLY for super-admins (canonical predicate lives in the
  // CountryContext). Non-super staff are fully locked: no switcher, and
  // activeCountry === homeCountry always. `availableCountries` is the switcher's
  // option list; flag assets are resolved from the active country code.
  const {
    activeCountry,
    canSwitch,
    availableCountries,
    setActiveCountry,
  } = useCountry();

  // ── Seller review queue (pending verification) ────────────────────────────
  // Server-owned count of sellers awaiting verification in the active country.
  // Loaded from the database on every page load and kept live over the navbar
  // socket, so it survives reloads and cannot be dismissed while sellers are
  // still waiting. Only Lusuku staff who may review sellers are subscribed.
  const isSellerReviewer = canReviewSellers(user?.result);
  const sellerQueue = useSellerReviewQueueSync({ user, socket, activeCountry });
  const pendingSellers = isSellerReviewer ? sellerQueue.pending : 0;

  // ── Sidebar menu badges ───────────────────────────────────────────────────
  // Mounted here, once, for the same reason the seller queue is: this navbar
  // is the only component that exists on every admin page and already holds a
  // connected socket. Mounting it in the Sidebar instead would be tempting but
  // the Sidebar remounts on navigation, which would re-request every count on
  // every page change.
  useMenuBadgeSync({
    user,
    socket,
    isLusukuStaff: user?.result?.userType === "LUSUKU STAFF",
    withInbox: !isBranchScoped,
  });

  // ── Persistent new-work nudge ─────────────────────────────────────────────
  // Reads the badge counts above and keeps nudging (tab title, favicon dot,
  // banner and pulsing bell) until the count that
  // raised it reaches zero. Nothing here can be dismissed into silence: only
  // doing the work clears it. An account with no waiting work gets `active:
  // false` and every one of those nudges stays off.
  const attention = useAttentionNudge();
  const openAttention = () => {
    if (attention.path) history.push(attention.path);
  };

  // Honour the market's "New seller / Desktop" toggle from the Notifications
  // Center. Fails open, so an operator who has never touched the settings still
  // gets the popup; only an explicit false silences it.
  const [sellerDesktopAlerts, setSellerDesktopAlerts] = useState(true);
  useEffect(() => {
    if (!isSellerReviewer) return undefined;
    let alive = true;
    const read = () => {
      fetchNotificationClientConfig().then((config) => {
        if (alive) {
          setSellerDesktopAlerts(isEventChannelEnabled(config, "newSeller", "desktop"));
        }
      });
    };
    read();
    window.addEventListener(ORDER_ALERTS_CONFIG_EVENT, read);
    return () => {
      alive = false;
      window.removeEventListener(ORDER_ALERTS_CONFIG_EVENT, read);
    };
  }, [isSellerReviewer]);

  // Switch the active operational country (super-admin only). Persist the new
  // ISO-2 via the context, then hard-reload so EVERY view refetches under the
  // new `x-country` header — the simplest reliable way to purge stale
  // country-scoped data (including anything cached in Redux) in one shot.
  const handleCountrySwitch = (iso2) => {
    if (!iso2 || iso2 === activeCountry) return;
    setActiveCountry(iso2);
    window.location.reload();
  };

  useEffect(() => {
    const nextUser = getUserFromLocalStorage();
    setUser((currentUser) => {
      // Route changes re-read the encrypted profile. Decryption returns a new
      // object even when the session is unchanged; replacing state in that case
      // rebuilt both navbar sockets on every navigation and closed WebSockets
      // while their upgrade was still in flight.
      try {
        return JSON.stringify(currentUser) === JSON.stringify(nextUser)
          ? currentUser
          : nextUser;
      } catch (_error) {
        return nextUser;
      }
    });
  }, [location]);

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      const nextState = await getSubscriptionState();
      if (mounted) setWebPushState(nextState);
    };
    const handleStateChange = (event) => {
      if (mounted) setWebPushState(event.detail || {});
    };
    const handleVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    refresh();
    window.addEventListener(WEB_PUSH_STATE_EVENT, handleStateChange);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      mounted = false;
      window.removeEventListener(WEB_PUSH_STATE_EVENT, handleStateChange);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, []);

  useEffect(() => {
    const handlePreferenceChange = (event) => {
      setOrderAlertsEnabled(event.detail?.enabled === true);
    };
    const handleStorage = (event) => {
      if (event.key === ORDER_ALERTS_ENABLED_KEY) {
        setOrderAlertsEnabled(event.newValue === "true");
      }
    };

    window.addEventListener(ORDER_ALERTS_STATE_EVENT, handlePreferenceChange);
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener(
        ORDER_ALERTS_STATE_EVENT,
        handlePreferenceChange
      );
      window.removeEventListener("storage", handleStorage);
    };
  }, []);



  // Fetch initial count of PENDING_3PL orders for 3PL partners
  useEffect(() => {
    const partnerId = user?.result?.partnerId || user?.result?._id;
    const token = user?.token;
    const isPartner = user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF' || !!user?.result?.partnerId;

    if (!isPartner || !partnerId || !token || assignedCountFetchedRef.current) return;

    const fetchAssignedCount = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/3pl-partners/${partnerId}/dashboard`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          const newAssigned = response.data.data?.orders?.newAssigned || 0;
          setAssignedOrdersCount(newAssigned);
          assignedCountFetchedRef.current = true;

          // Pre-populate a notification if there are pending orders on load
          if (newAssigned > 0) {
            const pendingNotification = {
              id: `pending-3pl-${Date.now()}`,
              message: `${newAssigned} order${newAssigned > 1 ? 's' : ''} awaiting rider assignment`,
              timestamp: new Date(),
              read: false,
            };
            setNotifications((prev) => {
              // Avoid duplicating on re-renders
              if (prev.some(n => n.id?.toString().startsWith('pending-3pl-'))) return prev;
              return [pendingNotification, ...prev].slice(0, 10);
            });
          }
        }
      } catch (err) {
        console.error('[AdminNavbar] Error fetching assigned orders count:', err);
      }
    };

    fetchAssignedCount();
  }, [user]);

  // Handler for new order notifications
  const handleNewOrder = useCallback((orderData) => {
    const order = orderData?.order || orderData;
    const partnerId = user?.result?.partnerId;
    const isPartner = partnerId || user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF';

    // For 3PL partners, increment the assigned orders count and show actionable notification
    if (isPartner) {
      setAssignedOrdersCount((prev) => prev + 1);
      const notification = {
        id: order?._id || Date.now(),
        orderId: order?._id,
        message: 'New order — assign a rider',
        timestamp: new Date(),
        read: false,
        status: 'pending', // actionable state
      };
      setNotifications((prev) => [notification, ...prev].slice(0, 15));
    } else {
      const notification = {
        id: order?._id || Date.now(),
        orderId: order?._id,
        message: 'New order received!',
        timestamp: new Date(),
        read: false,
        status: 'pending',
      };
      setNotifications((prev) => [notification, ...prev].slice(0, 15));
      // The server put this socket in the vendor's own order room, so an
      // event here is this business's order: ring, whatever page is open.
      // Partners keep their own sound (order vs recall) in the 3PL views.
      const role = user?.result?.role;
      notifyRef.current({
        title: "New order received",
        body: "A new order just arrived. Tap to view.",
        url:
          role === "restaurantVendor"
            ? "/admin/restaurant-vendor-dashboard"
            : role === "healthcareVendor"
              ? "/admin/healthcare-orders"
              : "/admin/retailerWholesalerOrdersTable",
        tag: order?._id ? `order-${order._id}` : "new-order",
      });
    }
  }, [user]);

  // Platform staff: the authoritative new-order event. The server emits it on
  // the actual order create (orderController) and on a ticket sale, to the
  // country dashboard room this socket joins through useMenuBadgeSync.
  const handleStaffOrderAlert = useCallback((info) => {
    const shortId = info?.orderId ? String(info.orderId).slice(-6).toUpperCase() : "";
    if (info?.kind === "TICKET") {
      const count = Number(info.count) || 1;
      notifyRef.current({
        title: count === 1 ? "New ticket sale" : `New ticket sale: ${count} tickets`,
        body: `${info.eventTitle || "A ticketed event"}. Tap to view the order.`,
        url: info?.orderId ? `/admin/ticketOrderDetails/${info.orderId}` : "/admin/ticketing/orders",
        tag: info?.orderId ? `order-${info.orderId}` : "new-ticket-sale",
      });
      return;
    }
    notifyRef.current({
      title: "New order received",
      body: shortId ? `Order #${shortId} just came in. Tap to view.` : "A new order just arrived.",
      url: "/admin/orders",
      tag: info?.orderId ? `order-${info.orderId}` : "new-order",
    });
  }, []);

  // Handler for order recalled from 3PL — resolve the notification
  const handleOrderRecalled = useCallback((data) => {
    const orderId = data?.orderId;
    setAssignedOrdersCount((prev) => Math.max(0, prev - 1));
    if (orderId) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.orderId === orderId
            ? { ...n, message: '✗ Order recalled by vendor', status: 'resolved', read: true }
            : n
        )
      );
    }
  }, []);

  const handlePosTerminalConnectivity = useCallback((event = {}) => {
    if (!event.terminalId) return;
    const offline = event.status === "OFFLINE";
    const notification = {
      id: `pos-connectivity-${event.terminalId}`,
      type: "pos_connectivity",
      terminalId: event.terminalId,
      message: offline
        ? `${event.label || event.terminalId} lost internet connectivity`
        : `${event.label || event.terminalId} is back online`,
      timestamp: new Date(event.at || Date.now()),
      read: !offline,
      status: offline ? "pending" : "resolved",
    };
    setNotifications((previous) => [
      notification,
      ...previous.filter((item) => item.id !== notification.id),
    ].slice(0, 15));
  }, []);

  // Handler for order status changes — resolve notifications when order is actioned
  const handleOrderStatusChange = useCallback((data) => {
    const { orderId, status } = data || {};
    if (!orderId || !status) return;

    const partnerId = user?.result?.partnerId;
    const isPartner = partnerId || user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF';
    if (!isPartner) return;

    // When an order moves past PENDING_3PL, it's been actioned — resolve the notification
    const actionedStatuses = ['ASSIGNED', 'ACCEPTED', 'PICKED_UP', 'DISPATCHED', 'IN_TRANSIT', 'DELIVERED'];
    if (actionedStatuses.includes(status?.toUpperCase())) {
      setNotifications((prev) => {
        const hasMatch = prev.some((n) => n.orderId === orderId && n.status === 'pending');
        if (!hasMatch) return prev;
        return prev.map((n) =>
          n.orderId === orderId && n.status === 'pending'
            ? { ...n, message: `✓ Order ${status.toLowerCase().replace('_', ' ')}`, status: 'resolved', read: true }
            : n
        );
      });
      // Decrement only if this was a PENDING_3PL → next status transition
      if (['ASSIGNED', 'ACCEPTED'].includes(status?.toUpperCase())) {
        setAssignedOrdersCount((prev) => Math.max(0, prev - 1));
      }
    }
  }, [user]);

  // Socket connection and event listeners
  useEffect(() => {
    if (!user?.result) return;

    const newSocket = io(process.env.REACT_APP_SERVER_URL, {
      auth: (callback) => callback({ token: getTokenFromLocalStorage() }),
      transports: ["polling", "websocket"],
      reconnection: true,
    });
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("[AdminNavbar] Socket connected");

      const partnerId = user?.result?.partnerId;
      const userId = user?.result?._id;

      // Join room based on user type. A vendor login (owner or team member)
      // joins its order rooms through useVendorOrderRooms below, which names
      // the owner and lets the server pick whole-business or branch rooms.
      if (partnerId) {
        // 3PL Partner
        newSocket.emit("join3PLPartnerRoom", partnerId);
      } else if (user?.result?.userType !== "SELLER" && userId) {
        newSocket.emit("joinVendorRoom", userId);
      }
    });

    // Listen for new order events based on user type
    const partnerId = user?.result?.partnerId;
    const role = user?.result?.role;

    if (partnerId) {
      // 3PL Partner events
      newSocket.on("order:assignedTo3PL", handleNewOrder);
      newSocket.on("order:recalledFrom3PL", handleOrderRecalled);
      // Listen for status changes to auto-resolve notifications
      newSocket.on("order:status_changed", handleOrderStatusChange);
      newSocket.on("order:accepted", handleOrderStatusChange);
    } else if (role === "restaurantVendor") {
      newSocket.on("newRestaurantOrder", handleNewOrder);
    } else if (role === "healthcareVendor") {
      newSocket.on("newHealthcareOrder", handleNewOrder);
    } else {
      // Retailer/Wholesaler
      newSocket.on("newOrder", handleNewOrder);
    }
    newSocket.on("pos:terminal_connectivity", handlePosTerminalConnectivity);
    if (user?.result?.userType === "LUSUKU STAFF") {
      newSocket.on("newOrderAlert", handleStaffOrderAlert);
    }

    return () => {
      newSocket.off("newOrderAlert", handleStaffOrderAlert);
      if (partnerId) {
        newSocket.off("order:assignedTo3PL");
        newSocket.off("order:recalledFrom3PL");
        newSocket.off("order:status_changed");
        newSocket.off("order:accepted");
      } else if (role === "restaurantVendor") {
        newSocket.off("newRestaurantOrder");
      } else if (role === "healthcareVendor") {
        newSocket.off("newHealthcareOrder");
      } else {
        newSocket.off("newOrder");
      }
      newSocket.off("pos:terminal_connectivity", handlePosTerminalConnectivity);
      newSocket.disconnect();
    };
  }, [
    user,
    handleNewOrder,
    handleStaffOrderAlert,
    handleOrderRecalled,
    handleOrderStatusChange,
    handlePosTerminalConnectivity,
  ]);

  // ----- WhatsApp / chat inbox notifications -----
  // Vendor whose inbox this user belongs to (sub-users inherit the parent vendor).
  // None for a member limited to some branches: the inbox is head office's and
  // the server refuses them its room (4 Oct 2026).
  const inboxVendorId = isBranchScoped
    ? null
    : user?.result?.parentVendorId || user?.result?._id || user?.result?.id;

  // Mark every notification for a conversation as attended (read) — called when
  // an agent replies, claims, or closes the chat, so the badge self-clears.
  const markConversationAttended = useCallback((conversationId) => {
    if (!conversationId) return;
    setChatNotifs((prev) =>
      prev.map((n) =>
        String(n.conversationId) === String(conversationId)
          ? { ...n, read: true }
          : n
      )
    );
  }, []);

  useEffect(() => {
    if (!user?.result || !inboxVendorId) return;

    // Signed in, like the navbar's own socket above: the server admits only
    // the business's own logins to its inbox room (joinVendorInbox), so an
    // anonymous socket was refused and no chat notification ever arrived.
    const inboxSocket = io(process.env.REACT_APP_SERVER_URL, socketAuthOptions({ userId: user?.result?._id }));

    inboxSocket.on("connect", () => {
      inboxSocket.emit("joinVendorInbox", { vendorId: inboxVendorId });
    });

    const onInbound = (envelope) => {
      const conversationId = envelope?.conversationId;
      const convo = envelope?.payload?.conversation || {};
      const name =
        convo.contactName ||
        convo.customerDisplayName ||
        convo.customerPhone ||
        "New WhatsApp message";
      if (!conversationId) return;
      setChatNotifs((prev) => {
        // Collapse repeated inbound from the same conversation into one entry.
        const existing = prev.find(
          (n) => String(n.conversationId) === String(conversationId)
        );
        const entry = {
          id: conversationId,
          conversationId,
          message: `New chat from ${name}`,
          timestamp: new Date(),
          read: false,
          count: (existing?.count || 0) + 1,
        };
        const rest = prev.filter(
          (n) => String(n.conversationId) !== String(conversationId)
        );
        return [entry, ...rest].slice(0, 20);
      });
    };

    const onAttended = (envelope) => markConversationAttended(envelope?.conversationId);

    inboxSocket.on("inbox:message_received", onInbound);
    inboxSocket.on("inbox:message_sent", onAttended);
    inboxSocket.on("inbox:conversation_assigned", onAttended);
    inboxSocket.on("inbox:conversation_closed", onAttended);

    return () => {
      inboxSocket.emit("leaveVendorInbox", { vendorId: inboxVendorId });
      inboxSocket.off("inbox:message_received", onInbound);
      inboxSocket.off("inbox:message_sent", onAttended);
      inboxSocket.off("inbox:conversation_assigned", onAttended);
      inboxSocket.off("inbox:conversation_closed", onAttended);
      inboxSocket.disconnect();
    };
  }, [user, inboxVendorId, markConversationAttended]);

  const chatUnread = chatNotifs.filter((n) => !n.read).length;

  const handleChatNotifClick = (notif) => {
    setChatNotifs((prev) =>
      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
    );
    history.push(`/admin/omnichannel-inbox?c=${notif.conversationId}`);
  };

  const clearChatNotifs = () => setChatNotifs([]);

  // A seller just joined the queue: add an entry to the bell list (it does not
  // count towards the badge, the server count already does) and, when this tab
  // is in the background, raise a desktop notification. Same tag as the Web
  // Push the server may also send, so the browser collapses the two into one.
  // When a seller leaves the queue, its entry is marked resolved.
  const lastSellerEvent = sellerQueue.lastEvent;
  useEffect(() => {
    if (!isSellerReviewer || !lastSellerEvent) return;
    const { reason, seller } = lastSellerEvent;
    const sellerId = seller?._id ? String(seller._id) : null;

    if (isArrivalReason(reason)) {
      const entry = {
        id: sellerId ? `seller-review-${sellerId}` : `seller-review-${Date.now()}`,
        type: "seller_review",
        sellerId,
        message: `${describeSeller(seller)} registered and is awaiting verification`,
        timestamp: new Date(lastSellerEvent.at || Date.now()),
        read: false,
        status: "pending",
      };
      setNotifications((prev) =>
        [entry, ...prev.filter((n) => n.id !== entry.id)].slice(0, 15)
      );

      if (
        sellerDesktopAlerts &&
        typeof window !== "undefined" &&
        "Notification" in window &&
        window.Notification.permission === "granted" &&
        document.visibilityState === "hidden"
      ) {
        try {
          const popup = new window.Notification("New seller awaiting verification", {
            body: `${describeSeller(seller)} just registered. ${formatPendingSellersMessage(
              lastSellerEvent.pending
            )}.`,
            tag: entry.id,
            icon: "/logo192.png",
            badge: "/badge72.png",
          });
          popup.onclick = () => {
            window.focus();
            history.push(PENDING_SELLERS_PATH);
            popup.close();
          };
        } catch (_error) {
          /* the badge still carries the count */
        }
      }
      return;
    }

    if (isDepartureReason(reason) && sellerId) {
      const verb =
        reason === "VERIFIED"
          ? "verified"
          : reason === "REJECTED"
          ? "rejected"
          : reason === "DELETED"
          ? "removed"
          : "deactivated";
      setNotifications((prev) =>
        prev.map((n) =>
          n.type === "seller_review" && n.sellerId === sellerId && n.status === "pending"
            ? {
                ...n,
                message: `${describeSeller(seller)} ${verb}`,
                status: "resolved",
                read: true,
              }
            : n
        )
      );
    }
    // `lastSellerEvent` carries a seq, so a repeat of the same reason still
    // fires; `history` is a stable router object and `setNotifications` is a
    // stable setter, so this dependency list is complete as written.
  }, [isSellerReviewer, lastSellerEvent, history, sellerDesktopAlerts]);

  const openPendingSellers = () => {
    setNotifications((prev) =>
      prev.map((n) => (n.type === "seller_review" ? { ...n, read: true } : n))
    );
    history.push(PENDING_SELLERS_PATH);
  };

  // Handle notification click - navigate to orders and mark as read
  const handleNotificationClick = (notification) => {
    const partnerId = user?.result?.partnerId;
    const isPartner = partnerId || user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF';
    const role = user?.result?.role;

    // Mark as read
    setNotifications((prev) =>
      prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
    );
    if (notification.type === "pos_connectivity") return;
    if (notification.type === "seller_review") {
      history.push(PENDING_SELLERS_PATH);
      return;
    }

    // For 3PL partners, reset the assigned orders count when they view orders
    if (isPartner) {
      setAssignedOrdersCount(0);
    }

    // Navigate based on user type
    if (isPartner) {
      history.push("/admin/partner-orders");
    } else if (role === "restaurantVendor") {
      history.push("/admin/restaurant-orders");
    } else if (role === "healthcareVendor") {
      history.push("/admin/healthcare-orders");
    } else {
      history.push("/admin/retailerWholesalerOrdersTable");
    }
  };

  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([]);
    // Also reset assigned orders count for 3PL partners
    const isPartner = user?.result?.partnerId || user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF';
    if (isPartner) {
      setAssignedOrdersCount(0);
    }
  };

  // Format timestamp for display
  const formatTime = (date) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(date)) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(date).toLocaleDateString();
  };

  // Get unread notification count. Seller-review entries are excluded: the
  // server's pending count (pendingSellers) already represents them, and a
  // seller must never be counted twice.
  const unreadCount = notifications.filter(
    (n) => !n.read && n.type !== "seller_review"
  ).length;

  // Unified notification badge: role-aware count
  // 3PL partners see assignedOrdersCount, other roles see unreadCount plus the
  // persistent number of sellers awaiting verification (staff reviewers only).
  const isPartnerUser = user?.result?.partnerId || user?.result?.operationMode === '3PL_PARTNER' || user?.result?.operationMode === '3PL_STAFF';
  const badgeCount = isPartnerUser ? assignedOrdersCount : unreadCount + pendingSellers;

  const login = () => {
    history.push("/auth/login-page");
  };

  // const logout = () => {
  //   dispatch({ type: LOGOUT });

  //   history.push("/auth/login-page");
  //   window.location.reload(true);
  // };
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('profile');
    window.location.href = "/auth/login-page";
  };

  return (
    <>
      <Navbar expand="lg">
        <Container fluid>
          <div className="navbar-wrapper">
            <div className="navbar-minimize">
              <Button
                className="btn-fill btn-round btn-icon d-none d-lg-block bg-dark border-dark"
                variant="dark"
                onClick={() => document.body.classList.toggle("sidebar-mini")}
              >
                <i className="fas fa-ellipsis-v visible-on-sidebar-regular"></i>
                <i className="fas fa-bars visible-on-sidebar-mini"></i>
              </Button>
              <Button
                className="btn-fill btn-round btn-icon d-block d-lg-none bg-dark border-dark"
                aria-label="Open navigation"
                variant="dark"
                onClick={() =>
                  document.documentElement.classList.toggle("nav-open")
                }
              >
                <i className="fas fa-ellipsis-v visible-on-sidebar-regular"></i>
                <i className="fas fa-bars visible-on-sidebar-mini"></i>
              </Button>
            </div>
            <Navbar.Brand href="#pablo" onClick={(e) => e.preventDefault()}>
              {/* Toggle Button and text to be here! */}
            </Navbar.Brand>
          </div>
          <button
            className="navbar-toggler navbar-toggler-right border-0"
            aria-label="Show dashboard actions"
            aria-expanded={collapseOpen}
            type="button"
            onClick={() => setCollapseOpen(!collapseOpen)}
          >
            <span className="navbar-toggler-bar burger-lines"></span>
            <span className="navbar-toggler-bar burger-lines"></span>
            <span className="navbar-toggler-bar burger-lines"></span>
          </button>
          <Navbar.Collapse
            className="justify-content-end admin-navbar-actions"
            in={collapseOpen}
          >
            <Nav.Link
              onClick={(e) => {
                e.preventDefault();
                history.push("/admin/cart-page");
              }}
            >
              <Nav.Item style={{ color: "#000002" }}>
                <span
                  style={{
                    position: "relative",
                    backgroundColor: "#1a8300",
                    textAlign: "center",
                    borderRadius: "10px",
                    minWidth: "18px",
                    padding: "0 5px",
                    height: "18px",
                    fontSize: "14px",
                    color: "#FFFFFF",
                    fontWeight: "bold",
                    lineHeight: "18px",
                    top: "-10px",
                  }}
                >
                  {getCartCount()}
                </span>
                <i className="nc-icon nc-cart-simple"></i>
                {""} Cart
              </Nav.Item>
            </Nav.Link>
            <Nav navbar className="navbar-right admin-navbar-toolbar">
              {/* Which branch the screens show (4 Oct 2026). Renders nothing
                  for a vendor without branches and for platform staff; a
                  member left with no branch sees "No branch". */}
              {user?.result && (hasBranches || noBranchAccess) && (
                <Nav.Item className="d-flex align-items-center">
                  <BranchPicker />
                </Nav.Item>
              )}
              {/* ── Country switcher / badge (multi_national) ──────────────
                  Super-admins (canSwitch === true) get a dropdown to change
                  the active operational country; on select we persist + hard
                  reload so every view refetches under the new x-country.
                  Non-super staff see a small READ-ONLY badge of their locked
                  country — never a switcher. Nothing renders when logged out. */}
              {user?.result && canSwitch && (
                <Dropdown as={Nav.Item}>
                  <Dropdown.Toggle
                    as={Nav.Link}
                    id="dropdown-country-switcher"
                    variant="default"
                    title="Switch operational country"
                  >
                    <CountryFlag country={activeCountry} width={22} decorative style={{ marginRight: 4 }} />
                    <span style={{ fontWeight: 500, color: "#23282d" }}>{activeCountry}</span>
                    <span className="d-lg-none" style={{ color: "#23282d" }}> Country</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    align="end"
                    style={{ minWidth: "200px", marginTop: "8px" }}
                    className="dropdown-menu-right"
                  >
                    <Dropdown.Header style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                      Active country
                    </Dropdown.Header>
                    {(availableCountries || []).map((iso2) => {
                      const m = getMarket(iso2);
                      const isActive = iso2 === activeCountry;
                      return (
                        <Dropdown.Item
                          key={iso2}
                          active={isActive}
                          onClick={() => handleCountrySwitch(iso2)}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            fontSize: "0.9rem",
                            padding: "8px 16px",
                            fontWeight: isActive ? 600 : 500,
                            // App theme: gold accent (#f2c811) for the active row
                            // with high-contrast dark text; gold left bar for emphasis.
                            backgroundColor: isActive ? "#f2c811" : "transparent",
                            color: "#23282d",
                            borderLeft: isActive ? "3px solid #e0b800" : "3px solid transparent",
                          }}
                        >
                          <CountryFlag country={iso2} width={24} decorative />
                          <span style={{ flex: 1, color: "#23282d" }}>{m?.name}</span>
                          <span style={{ color: isActive ? "#6b5900" : "#9aa0a6", fontSize: "0.8rem", fontWeight: 600 }}>{iso2}</span>
                          {isActive && (
                            <i className="nc-icon nc-check-2" style={{ color: "#1f7a1f" }} />
                          )}
                        </Dropdown.Item>
                      );
                    })}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              {user?.result && !canSwitch && (
                <Nav.Item className="d-flex align-items-center">
                  <span
                    title="Your operational country"
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "5px",
                      padding: "2px 10px",
                      marginRight: "4px",
                      borderRadius: "12px",
                      backgroundColor: "#f1f3f5",
                      color: "#495057",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      lineHeight: "20px",
                    }}
                  >
                    <CountryFlag country={activeCountry} width={20} decorative />
                    {activeCountry}
                  </span>
                </Nav.Item>
              )}
              {user?.result && (
                <Dropdown as={Nav.Item}>
                  <Dropdown.Toggle
                    as={Nav.Link}
                    id="dropdown-whatsapp-chats"
                    variant="default"
                    title="WhatsApp chats"
                  >
                    <i className="nc-icon nc-chat-round mr-1"></i>
                    {chatUnread > 0 && (
                      <span
                        style={{
                          position: "absolute",
                          top: "2px",
                          right: "8px",
                          backgroundColor: "#25D366",
                          color: "#fff",
                          borderRadius: "10px",
                          padding: "1px 6px",
                          fontSize: "11px",
                          fontWeight: "bold",
                          minWidth: "18px",
                          textAlign: "center",
                          lineHeight: "16px",
                          display: "inline-block",
                          zIndex: 1,
                        }}
                      >
                        {chatUnread}
                      </span>
                    )}
                    <span className="d-lg-none">Chats</span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    align="end"
                    style={{ minWidth: "300px", marginTop: "8px" }}
                    className="dropdown-menu-right"
                  >
                    {chatNotifs.length === 0 ? (
                      <Dropdown.Item disabled style={{ textAlign: "center", color: "#888" }}>
                        No new chats
                      </Dropdown.Item>
                    ) : (
                      <>
                        {chatNotifs.map((notif) => (
                          <Dropdown.Item
                            key={notif.id}
                            onClick={() => handleChatNotifClick(notif)}
                            style={{
                              backgroundColor: notif.read ? "transparent" : "#f0fff4",
                              borderLeft: notif.read ? "3px solid transparent" : "3px solid #25D366",
                              fontSize: "0.85rem",
                              padding: "10px 16px",
                              whiteSpace: "normal",
                            }}
                          >
                            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                              <span style={{
                                display: "inline-block", width: "8px", height: "8px",
                                borderRadius: "50%", backgroundColor: notif.read ? "#ccc" : "#25D366",
                                marginTop: "5px", flexShrink: 0,
                              }} />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: notif.read ? "normal" : "600", color: "#333" }}>
                                  {notif.message}
                                  {notif.count > 1 ? ` (${notif.count})` : ""}
                                </div>
                                <small style={{ color: "#999" }}>{formatTime(notif.timestamp)}</small>
                              </div>
                            </div>
                          </Dropdown.Item>
                        ))}
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={clearChatNotifs}
                          style={{ textAlign: "center", color: "#dc3545", fontSize: "0.85rem" }}
                        >
                          Clear all
                        </Dropdown.Item>
                      </>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              )}
              <Dropdown as={Nav.Item}>
                <Dropdown.Toggle
                  as={Nav.Link}
                  id="dropdown-414718872"
                  variant="default"
                  className={`global-notification-toggle ${
                    orderAlertsEnabled ? "is-enabled" : "is-disabled"
                  }${attention.active ? " has-attention" : ""}`}
                  title={
                    // The pulse needs a text equivalent, or it says nothing at
                    // all to anyone who cannot see it or has motion turned off.
                    attention.active
                      ? attention.message
                      : orderAlertsEnabled
                      ? "Order notifications enabled"
                      : "Order notifications disabled"
                  }
                >
                  <span className="global-notification-icon">
                    <i
                      className={
                        orderAlertsEnabled ? "fas fa-bell" : "fas fa-bell-slash"
                      }
                    />
                    <span className="global-notification-state-dot" />
                  </span>
                  {badgeCount > 0 && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '2px',
                        right: '8px',
                        backgroundColor: isPartnerUser ? '#ff5722' : '#e74c3c',
                        color: '#fff',
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        minWidth: '18px',
                        textAlign: 'center',
                        lineHeight: '16px',
                        display: 'inline-block',
                        zIndex: 1,
                      }}
                    >
                      {badgeCount}
                    </span>
                  )}
                  <span className="d-lg-none">Notification</span>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  align="end"
                  style={{
                    minWidth: "280px",
                    marginTop: "8px",
                    "--bs-dropdown-arrow-offset": "calc(100% - 24px)"
                  }}
                  className="dropdown-menu-right"
                >
                  <Dropdown.Item
                    className="global-notification-status"
                    onClick={() => history.push("/admin/notifications")}
                  >
                    <span
                      className={`global-notification-status__icon ${
                        orderAlertsEnabled ? "is-enabled" : "is-disabled"
                      }`}
                    >
                      <i
                        className={
                          orderAlertsEnabled
                            ? "fas fa-bell"
                            : "fas fa-bell-slash"
                        }
                      />
                    </span>
                    <span>
                      <strong>
                        {orderAlertsEnabled
                          ? "Order alerts enabled"
                          : "Order alert sound disabled"}
                      </strong>
                      <small>
                        {!orderAlertsEnabled
                          ? "Open Notifications to enable sound"
                          : webPushState.subscribed && webPushState.enabled
                          ? "Sound and Web Push are active"
                          : webPushState.subscribed
                          ? "Sound is on; server push is paused"
                          : "Sound is on; Web Push is not enabled"}
                      </small>
                    </span>
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  {pendingSellers > 0 && (
                    <>
                      {/* Pinned: derived from the server count, so it survives a
                          reload and "Clear all" and only disappears once the
                          queue is empty. */}
                      <Dropdown.Item
                        onClick={openPendingSellers}
                        className="seller-review-pinned"
                        title="Open the Pending tab of the Seller Directory"
                      >
                        <div className="seller-review-pinned__row">
                          <span className="seller-review-pinned__count">{pendingSellers}</span>
                          <div className="seller-review-pinned__body">
                            <div className="seller-review-pinned__title">
                              {formatPendingSellersMessage(pendingSellers)}
                            </div>
                            {sellerQueue.sellers.length > 0 && (
                              <small className="seller-review-pinned__names">
                                {sellerQueue.sellers
                                  .slice(0, 3)
                                  .map((seller) => seller.businessName || seller.email)
                                  .filter(Boolean)
                                  .join(", ")}
                                {pendingSellers > 3 ? ` and ${pendingSellers - 3} more` : ""}
                              </small>
                            )}
                          </div>
                          <span className="seller-review-pinned__cta">Review</span>
                        </div>
                      </Dropdown.Item>
                      <Dropdown.Divider />
                    </>
                  )}
                  {notifications.length === 0 ? (
                    // Only when there is genuinely nothing to act on — saying
                    // "no new notifications" directly under a pending-seller
                    // row would contradict it.
                    pendingSellers > 0 ? null : (
                      <Dropdown.Item disabled style={{ textAlign: "center", color: "#888" }}>
                        No new notifications
                      </Dropdown.Item>
                    )
                  ) : (
                    <>
                      {notifications.map((notif) => {
                        const isPending = notif.status === 'pending';
                        const isResolved = notif.status === 'resolved';
                        return (
                          <Dropdown.Item
                            key={notif.id}
                            onClick={() => handleNotificationClick(notif)}
                            style={{
                              backgroundColor: isPending ? "#fff5f0" : isResolved ? "#f0faf0" : (notif.read ? "transparent" : "#f0f7ff"),
                              borderLeft: isPending ? "3px solid #ff5722" : isResolved ? "3px solid #4caf50" : "3px solid transparent",
                              fontSize: "0.85rem",
                              padding: "10px 16px",
                              whiteSpace: "normal",
                            }}
                          >
                            <div style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                            }}>
                              {/* Status indicator */}
                              <span style={{
                                display: "inline-block",
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: isPending ? "#ff5722" : isResolved ? "#4caf50" : "#007bff",
                                marginTop: "5px",
                                flexShrink: 0,
                              }} />
                              <div style={{ flex: 1 }}>
                                <div style={{
                                  fontWeight: isPending ? "600" : "normal",
                                  color: isResolved ? "#666" : "#333",
                                }}>
                                  {notif.message}
                                </div>
                                <small style={{ color: "#999" }}>
                                  {formatTime(notif.timestamp)}
                                </small>
                              </div>
                            </div>
                          </Dropdown.Item>
                        );
                      })}
                      <Dropdown.Divider />
                      <Dropdown.Item
                        onClick={clearNotifications}
                        style={{ textAlign: "center", color: "#dc3545", fontSize: "0.85rem" }}
                      >
                        Clear all
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
              <Dropdown as={Nav.Item}>
                <Dropdown.Toggle
                  as={Nav.Link}
                  id="dropdown-41471887333"
                  variant="default"
                >
                  <i className="nc-icon nc-bullet-list-67"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu
                  aria-labelledby="navbarDropdownMenuLink"
                  align="end"

                >
                  <Dropdown.Item
                    href="#pablo"
                    onClick={(e) => e.preventDefault()}
                  >
                    <i className="nc-icon nc-email-85"></i>
                    Messages
                  </Dropdown.Item>
                  <Dropdown.Item
                    href="#pablo"
                    onClick={(e) => e.preventDefault()}
                  >
                    <i className="nc-icon nc-umbrella-13"></i>
                    Call Center
                  </Dropdown.Item>
                  {user?.result ? (
                    <Dropdown.Item
                      className="text-danger"
                      href="#pablo"
                      onClick={logout}
                    >
                      <i className="nc-icon nc-button-power"></i>
                      Log out
                    </Dropdown.Item>
                  ) : (
                    <Dropdown.Item
                      className="text-danger"
                      href="#login"
                      onClick={login}
                    >
                      <i className="nc-icon nc-button-power"></i>
                      Log In
                    </Dropdown.Item>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      {attention.bannerVisible && (
        <AttentionBanner
          count={attention.count}
          message={attention.message}
          onOpen={openAttention}
          onHide={attention.dismissBanner}
        />
      )}
    </>
  );
}

export default AdminNavbar;
